import { Cancel, Save, Delete } from "@mui/icons-material";
import { AppBar, Box, Button, FormControlLabel, Paper, Switch, TextField, Toolbar, Typography } from "@mui/material";
import React, { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { getUnixTime } from 'date-fns';
import { AdapterDateFns } from "@mui/x-date-pickers/AdapterDateFns";
import { DatePicker, LocalizationProvider } from "@mui/x-date-pickers";
import { useDispatch, useSelector } from "react-redux";
import { uploadAudio, uploadImage } from "../../../actions/mediaActions";
import {
  addEditDailyReflections,
  closeDailyReflectionForm,
  fetchOneDailyReflection,
  deleteDailyReflection,
} from "../../../actions/dailyReflectionActions";
import LoadingBackdrop from "../../Common/LoadingBackdrop";
import Swal from "sweetalert2";
import ImageContainerPreview from "../../Common/ImageContainerPreview";
import AudioContainerPreview from "../../Common/AudioContainerPreview";
import AuthorSelector from "../../Common/AuthorSelector";
import TagsSection from "../../Common/TagsSection";
import MainContentContainer from "../../MainContentContainer/MainContentContainer";
import UniversalLinkField from "../../Common/UniversalLinkField";

export default function DailyReflectionForm() {
  const dispatch = useDispatch();
  const { id } = useParams();
  const navigate = useNavigate();

  const DAILY_INSPIRATION_OBJECT_TAG = "DailyInspiration";

  const mediaAudio = useSelector(store => store.media.audio);
  const mediaImage = useSelector(store => store.media.image);
  const loading = useSelector(state => state.daily_reflections.loading);
  const selectedDailyReflection = useSelector(state => state.daily_reflections.selectedDailyReflection);

  const [dailyReflection, setDailyReflection] = useState({
    date: "",
    title: "",
    author_id: "",
    deeplink_image: null,
    deeplink_image_url: "",
    audio_url: "",
    audio_length: 0,
    is_seasonal: false,
  });
  const [tags, setTags] = useState([]);

  useEffect(() => {
    fetchAndSetDailyReflection();
  }, [selectedDailyReflection]);

  const fetchAndSetDailyReflection = () => {
    if (!id) return;
    if (selectedDailyReflection) {
      setDailyReflection({
        ...selectedDailyReflection,
        date: new Date(selectedDailyReflection.date_epoch * 1000),
        author_id: selectedDailyReflection.author_id || "",
        is_seasonal: Boolean(selectedDailyReflection.is_seasonal),
      });
      loadTags(selectedDailyReflection.tags);
      return;
    }
    dispatch(fetchOneDailyReflection(id));
  };

  const loadTags = tags => {
    if (tags && tags.length > 0) {
      setTags(tags.map(tag => ({ tag: tag.tag })));
    }
  };

  const handleChange = e => {
    setDailyReflection({
      ...dailyReflection,
      [e.target.name]: e.target.value,
    });
  };

  const handleChangeDate = date => {
    setDailyReflection({
      ...dailyReflection,
      date,
    });
  };

  const handleChangeAuthor = value => {
    setDailyReflection(prev => ({
      ...prev,
      author_id: value,
    }));
  };

  const handleImageUpload = e => {
    const file = e.target.files[0];
    setDailyReflection({
      ...dailyReflection,
      deeplink_image: file,
    });
    dispatch(uploadImage(file));
  };

  const handleAudioUpload = e => {
    const file = e.target.files[0];
    dispatch(uploadAudio(file));
  };

  const handleSave = () => {
    /* Daily Inspirations now recur annually (Monday 12107892350). We
     * derive (schedule_month, schedule_day) from the picked date and
     * discard year. date_epoch is sent along during the API transition;
     * the APIv2 service treats (month, day) as the source of truth. */
    const data = {
      ...dailyReflection,
      date_epoch: dailyReflection.date ? getUnixTime(dailyReflection.date) : null,
      schedule_month: dailyReflection.date ? dailyReflection.date.getMonth() + 1 : null,
      schedule_day: dailyReflection.date ? dailyReflection.date.getDate() : null,
      is_seasonal: dailyReflection.is_seasonal ? 1 : 0,
    };
    dispatch(addEditDailyReflections(data, () => navigate("/daily-inspirations")));
  };

  const updateDuration = e => {
    const duration = Math.floor(e.target.duration) || 0;
    setDailyReflection({
      ...dailyReflection,
      audio_length: duration,
    });
  };

  useEffect(() => {
    handleImageUploaded();
  }, [mediaImage]);

  useEffect(() => {
    handleAudioUploaded();
  }, [mediaAudio]);

  const handleImageUploaded = () => {
    if (mediaImage) {
      setDailyReflection({
        ...dailyReflection,
        deeplink_image_url: mediaImage,
      });
    }
  };

  const handleAudioUploaded = () => {
    if (mediaAudio) {
      setDailyReflection({
        ...dailyReflection,
        audio_url: mediaAudio,
      });
    }
  };

  const handleTagsChange = newTags => {
    setTags(newTags);
    // Tags are now auto-applied via TagsSection component
  };

  const handleTagsRemove = tagToRemove => {
    // Tags are now auto-removed via TagsSection component
  };

  const handleCancel = () => {
    dispatch(closeDailyReflectionForm());
    navigate("/daily-inspirations");
  };

  const handleDelete = () => {
    Swal.fire({
      title: `Are you sure?`,
      text: "You will not be able to recover this record!",
      icon: "warning",
      showCancelButton: true,
      confirmButtonText: "Yes, delete it!",
      cancelButtonText: "No, cancel",
    }).then(result => {
      if (result.value) {
        dispatch(deleteDailyReflection(dailyReflection.daily_reflection_id, () => navigate("/daily-inspirations")));
      }
    });
  };

  const handleRemoveImage = () => {
    setDailyReflection({
      ...dailyReflection,
      deeplink_image: null,
      deeplink_image_url: null,
    });
  };

  const handleRemoveAudio = () => {
    setDailyReflection({
      ...dailyReflection,
      audio_url: null,
    });
  };

  return (
    <MainContentContainer>
      <LoadingBackdrop open={loading} />
      <AppBar position="static">
        <Toolbar>
          <Typography variant="h6" component="div" sx={{ flexGrow: 1 }}>
            {id ? "Edit" : "Create"} Daily Inspiration {id ? "" : ""}
          </Typography>
          <Button color="success" onClick={handleSave} startIcon={<Save />} variant="contained" sx={{ marginRight: 2 }}>
            Save
          </Button>
          <Button color="secondary" onClick={handleCancel} startIcon={<Cancel />} variant="contained">
            Cancel
          </Button>
        </Toolbar>
      </AppBar>
      <Box gap={4} sx={{ mt: 2 }}>
        <Paper sx={{ padding: 2 }}>
          <Box component="form" display="grid" gap={4}>
            {/* Basic Information Section */}
            <Box>
              <Typography variant="h6" gutterBottom>
                Basic Information
              </Typography>
              <Box display="grid" gridTemplateColumns={{ xs: "1fr", md: "repeat(3, 1fr)" }} gap={3}>
                <LocalizationProvider dateAdapter={AdapterDateFns}>
                  <DatePicker
                    name="date"
                    label="Scheduled For"
                    value={dailyReflection.date !== "" ? new Date(dailyReflection.date) : null}
                    onChange={handleChangeDate}
                    /* Feb 29 is reserved for the leap-year evergreen
                     * fallback (APIv2 pickEvergreenFallback). JS months
                     * are 0-indexed; February is month 1. */
                    shouldDisableDate={(d) => d.getMonth() === 1 && d.getDate() === 29}
                    slotProps={{
                      textField: {
                        helperText: "Recurs annually on this month + day (year is ignored). Feb 29 is reserved for the leap-year evergreen pick.",
                      },
                    }}
                  />
                </LocalizationProvider>
                <TextField
                  fullWidth
                  label="Title"
                  variant="outlined"
                  name="title"
                  value={dailyReflection.title}
                  onChange={handleChange}
                  autoFocus
                  required
                />
                <AuthorSelector value={dailyReflection.author_id} onChange={handleChangeAuthor} required />
              </Box>
            </Box>

            {/* Media Section */}
            <Box>
              <Typography variant="h6" gutterBottom>
                Media
              </Typography>
              <Box display="grid" gridTemplateColumns={{ xs: "1fr", md: "repeat(2, 1fr)" }} gap={4}>
                <Box>
                  <ImageContainerPreview
                    title="Deeplink Image"
                    image={
                      dailyReflection.deeplink_image
                        ? URL.createObjectURL(dailyReflection.deeplink_image)
                        : dailyReflection.deeplink_image_url
                    }
                    name="file"
                    handleRemove={handleRemoveImage}
                    handleImageUpload={handleImageUpload}
                    showCopyUrlButton={true}
                    sx={{ padding: 1, background: "#FFFCF5", height: 90, objectFit: "contain" }}
                  />
                  <Box sx={{ mt: 2 }}>
                    <UniversalLinkField url={dailyReflection.universal_link} />
                  </Box>
                </Box>
                <Box>
                  <AudioContainerPreview
                    title="Audio"
                    audio={dailyReflection.audio_url}
                    name="audio_url"
                    handleRemove={handleRemoveAudio}
                    handleAudioUpload={handleAudioUpload}
                    onLoadedData={updateDuration}
                    showCopyUrlButton={true}
                  />
                </Box>
              </Box>
            </Box>

            {/* Settings Section */}
            <Box>
              <Typography variant="h6" gutterBottom>
                Settings
              </Typography>
              <FormControlLabel
                control={
                  <Switch
                    checked={Boolean(dailyReflection.is_seasonal)}
                    onChange={(e) =>
                      setDailyReflection({
                        ...dailyReflection,
                        is_seasonal: e.target.checked,
                      })
                    }
                  />
                }
                label="Seasonal — tied to a holiday or season. Excluded from the Feb 29 fallback pool on leap years."
              />
            </Box>

            {/* Tags Section */}
            {dailyReflection && dailyReflection.daily_reflection_id > 0 && (
              <TagsSection
                tags={tags}
                onTagsChange={handleTagsChange}
                onTagsRemove={handleTagsRemove}
                objectId={dailyReflection.daily_reflection_id}
                objectType="DailyInspiration"
                title="Tags"
              />
            )}
          </Box>
          {id && (
            <Box sx={{ mt: 3, display: "flex", justifyContent: "right" }}>
              <Button color="error" onClick={handleDelete} startIcon={<Delete />} variant="contained" size="large">
                Delete Daily Inspiration
              </Button>
            </Box>
          )}
        </Paper>
      </Box>
    </MainContentContainer>
  );
}
